import { SelectorOptionPropsInterface, showNotification } from 'sonata-react-components';
import { getInsertionOrders, type InsertionOrder, type LineItem } from 'sonata-static-storage/lib/entities';

import { getLineItems } from '@/services/lineItem.service';

import type { IntlFormatters } from 'react-intl';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { VenueGroupInterface } from 'sonata-static-storage/lib/schemas/user/venueGroup.schema';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

export type VenueWithLinkedEntities = Venue & {
  linkedIos: InsertionOrder[];
  linkedLis: LineItem[];
};

export type RowAction = {
  icon: string;
  iconClassName: string;
  id: string;
  onClick: () => void;
  shape: 'link';
  size: 'ultraSmall';
  title: string;
  type: 'tertiary';
};

export type TaptapListItem = {
  actions: RowAction[];
  coordinates: string;
  groups: { color?: string; text?: string }[];
  id: string;
  linkedIOs: { _id: string; name: string }[];
  linkedLIs: { _id: string; name: string }[];
  title: string;
  venue: Venue;
};

export function getVenueGroupsOptions(user: UserInterface): SelectorOptionPropsInterface[] {
  return user.venueGroups.map(vg => ({
    id: vg._id as string,
    label: vg.name
  }));
}

export async function getVenuesWithLinkedEntities(venues: Venue[]): Promise<VenueWithLinkedEntities[]> {
  const getLinkedInsertionOrders = async (venue: Venue) => {
    if (!venue.insertionOrders?.length) {
      return [];
    }
    const paginatedInsertionOrders = await getInsertionOrders(venue.user, { ids: venue.insertionOrders });
    return paginatedInsertionOrders.items.map(io => io.entity);
  };
  const getLinkedLineItems = async (venue: Venue) => {
    if (!venue.campaigns?.length) {
      return [];
    }
    const paginatedLineItems = await getLineItems(venue.user, { ids: venue.campaigns });
    return paginatedLineItems.items;
  };

  return Promise.all(
    venues.map(async venue => {
      const [linkedIos, linkedLis] = await Promise.all([getLinkedInsertionOrders(venue), getLinkedLineItems(venue)]);

      return {
        ...venue,
        linkedIos,
        linkedLis
      } as VenueWithLinkedEntities;
    })
  );
}

export function formatVenueCoordinates(venue: Pick<Venue, 'latlng'>): string {
  return venue.latlng ? `${venue.latlng.lat}, ${venue.latlng.lng}` : 'N/A';
}

export function venueToRow(
  venue: VenueWithLinkedEntities,
  venueGroups: VenueGroupInterface<string>[],
  formatMessage: IntlFormatters['formatMessage']
): TaptapListItem {
  const { _id } = venue;
  const coordinates = formatVenueCoordinates(venue);
  return {
    actions: [
      {
        icon: 'fa-regular fa-copy',
        iconClassName: '',
        id: `venue-copy-${_id}`,
        onClick: async () => {
          await navigator.clipboard.writeText(coordinates);
          showNotification(
            'map',
            formatMessage({
              defaultMessage: 'Venue coordinates copied to clipboard',
              id: 'menu.myVenues.list.coordinatesCopy'
            })
          );
        },
        shape: 'link' as const,
        size: 'ultraSmall' as const,
        title: formatMessage({
          defaultMessage: 'Copy coordinates',
          id: 'menu.myVenues.list.copyCoordinates'
        }),
        type: 'tertiary' as const
      }
    ],
    coordinates,
    groups: venue.groups?.length
      ? venue.groups.flatMap(g => {
          const venueGroup = venueGroups.find(vg => vg._id?.toString() === g);
          return venueGroup ? [{ color: venueGroup.color, text: venueGroup.name }] : [];
        })
      : [],
    id: _id!,
    linkedIOs: venue.linkedIos.map(io => ({ _id: io._id!, name: io.name })),
    linkedLIs: venue.linkedLis.map(li => ({ _id: li._id!, name: li.name })),
    title: venue.name,
    venue
  };
}
